/** What the manual-entry form may offer, read from the factor library and from nothing else.
 *
 * **F06's defect, one screen over.** The form defaulted its carrier to `natural_gas`, so somebody
 * recording a diesel generator who did not change it filed a gas figure — about a factor of two
 * out, with a complete receipt. `evidence/classification.py` answers the same question on the
 * server with *"the factor set is the authority on what is supported"*: `supported_fuels` and
 * `supported_refrigerants` are read from the set, never from a second list. This is that rule on
 * the client. There is **no list of carriers in this file or in the screen**; what is offered is
 * whatever `GET /api/v1/factor-sets` says the facility's set publishes for the chosen category.
 *
 * **Nothing here substitutes.** A site whose jurisdiction no set covers gets no options — never
 * another jurisdiction's — and the server still refuses a carrier it cannot price
 * (`manual.record_manual`), so this narrows what a person can pick without becoming the authority.
 */

import type { FactorLibrary, FactorLibrarySet } from "../api/factors";

/** The activity kinds the manual form records, with the words a person knows them by.
 *
 * These are labels for the engine's `Category` values, not a claim about what is supported: a
 * category is offered only if the site's set has a factor for it (`categoriesIn`).
 */
export const MANUAL_CATEGORIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "stationary", label: "Fuel burned on site" },
  { value: "mobile", label: "Fuel burned in vehicles" },
  { value: "electricity", label: "Electricity purchased" },
  { value: "fugitive", label: "Refrigerant lost" },
];

/** The set that prices a site, or `undefined` — never a different one.
 *
 * A facility's jurisdiction is a country or a country-and-state (`GB`, `US`, `US-CA`) and a set's is
 * the country. The server's `SET_FOR_JURISDICTION` makes the same collapse; it is repeated here as
 * *exact, else the country before the hyphen* because no route serves that map. If a jurisdiction
 * is ever added whose set does not follow that shape, this returns nothing and the form offers
 * nothing — the safe direction — and the server stays the authority either way.
 */
export function setFor(library: FactorLibrary, jurisdiction: string): FactorLibrarySet | undefined {
  const exact = library.sets.find((set) => set.jurisdiction === jurisdiction);
  if (exact) return exact;
  const country = jurisdiction.split("-")[0];
  return library.sets.find((set) => set.jurisdiction === country);
}

/** The categories this set has at least one factor for, in the form's order. */
export function categoriesIn(set: FactorLibrarySet): Array<{ value: string; label: string }> {
  const present = new Set(set.factors.map((factor) => factor.category));
  return MANUAL_CATEGORIES.filter((category) => present.has(category.value));
}

/** The distinct carriers the set publishes for one category, sorted. */
export function carriersIn(set: FactorLibrarySet, category: string): string[] {
  return [
    ...new Set(
      set.factors.filter((factor) => factor.category === category).map((f) => f.fuel_or_carrier),
    ),
  ].sort();
}
